import { API_ENDPOINTS, ApiResponse } from '../api';
import httpClient from '../httpClient';
import * as XLSX from 'xlsx';

export interface YearlyCalendarCourseReport {
  trLCMSPlanningCoursesID: string;
  msLCMSCourseID: string;
  planID: string;
  planName: string;
  planStatus: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  statusCourse: string;
  notes: string | null;
  learningHours: number;
  planningStart: string;
  planningEnd: string;
  learningObjectives: string;
  schoolName: string;
  modeOfDelivery: string;
  assessmentStartDate: string | null;
  assessmentEndDate: string | null;
}

export interface YearlyCalendarReportFilters {
  startDate: string;
  endDate: string;
  planStatus?: string;
  deliveryType?: string;
  courseStatus?: string;
  schoolName?: string;
  search?: string;
  schoolID?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface YearlyCalendarReportStatistics {
  totalCourses: number;
  plannedCourses: number;
  canceledCourses: number;
  droppedCourses: number;
  totalLearningHours: number;
  coursesByDeliveryType: { [key: string]: number };
  coursesBySchool: { [key: string]: number };
  coursesByStatus: { [key: string]: number };
  monthlyDistribution: { [key: string]: number };
}

class YearlyCalendarReportService {
  private convertLocalDateToUTC(
    dateString: string | undefined,
    boundary: 'start' | 'end'
  ): string | undefined {
    if (!dateString) return undefined;

    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyPattern.test(dateString)) {
      const [yearStr, monthStr, dayStr] = dateString.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        const localDate = new Date(year, month - 1, day);

        if (boundary === 'start') {
          localDate.setHours(0, 0, 0, 0);
        } else {
          localDate.setHours(23, 59, 59, 999);
        }

        return localDate.toISOString();
      }
    }

    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? dateString : parsedDate.toISOString();
  }

  async getCourseReportList(
    filters: YearlyCalendarReportFilters
  ): Promise<ApiResponse<YearlyCalendarCourseReport>> {
    const startDate = this.convertLocalDateToUTC(filters.startDate, 'start');
    const endDate = this.convertLocalDateToUTC(filters.endDate, 'end');

    const params: any = {
      startDate,
      endDate,
      pageNumber: filters.pageNumber || 1,
      pageSize: filters.pageSize || 50
    };

    if (filters.planStatus) params.planStatus = filters.planStatus;
    if (filters.deliveryType) params.deliveryType = filters.deliveryType;
    if (filters.courseStatus) params.courseStatus = filters.courseStatus;
    if (filters.schoolName) params.schoolName = filters.schoolName;
    if (filters.search) params.search = filters.search;
    if (filters.schoolID) params.schoolID = filters.schoolID;

    const response = await httpClient.get<ApiResponse<YearlyCalendarCourseReport>>(
      API_ENDPOINTS.YEARLY_CALENDAR.COURSE_REPORTS,
      { params }
    );
    return response.data;
  }

  async getAllCoursesForExport(
    filters: Omit<YearlyCalendarReportFilters, 'pageNumber' | 'pageSize'>
  ): Promise<YearlyCalendarCourseReport[]> {
    try {
      const response = await this.getCourseReportList({
        ...filters,
        pageNumber: 1,
        pageSize: 10000
      } as YearlyCalendarReportFilters);
      return response.data || [];
    } catch {
      throw new Error('Failed to fetch courses for export');
    }
  }

  async exportToExcel(
    filters: Omit<YearlyCalendarReportFilters, 'pageNumber' | 'pageSize'>,
    filename?: string
  ): Promise<void> {
    try {
      const courses = await this.getAllCoursesForExport(filters);

      const excelData = courses.map((course, index) => ({
        'No.': index + 1,
        'Plan Name': course.planName || '-',
        'Plan Status': course.planStatus || '-',
        'Course Name': course.courseName || '-',
        'Course Code': course.courseCode || '-',
        School: course.schoolName || '-',
        'Delivery Type': course.deliveryType || '-',
        'Mode of Delivery': course.modeOfDelivery || '-',
        'Course Status': course.statusCourse || '-',
        'Learning Hours': course.learningHours || 0,
        'Planning Start': course.planningStart
          ? new Date(course.planningStart).toLocaleDateString()
          : '-',
        'Planning End': course.planningEnd
          ? new Date(course.planningEnd).toLocaleDateString()
          : '-',
        'Assessment Start': course.assessmentStartDate
          ? new Date(course.assessmentStartDate).toLocaleDateString()
          : '-',
        'Assessment End': course.assessmentEndDate
          ? new Date(course.assessmentEndDate).toLocaleDateString()
          : '-',
        'Learning Objectives': this.stripHtml(course.learningObjectives || ''),
        Notes: course.notes || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();

      ws['!cols'] = [
        { wch: 5 },
        { wch: 25 },
        { wch: 12 },
        { wch: 30 },
        { wch: 15 },
        { wch: 25 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 40 },
        { wch: 30 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Course Report');

      const dateRange = `${filters.startDate}_to_${filters.endDate}`;
      const defaultFilename = `yearly_calendar_course_report_${dateRange}.xlsx`;
      XLSX.writeFile(wb, filename || defaultFilename);
    } catch {
      throw new Error('Failed to export data to Excel');
    }
  }

  async exportCalendarToExcel(
    filters: Omit<YearlyCalendarReportFilters, 'pageNumber' | 'pageSize'>,
    filename?: string
  ): Promise<void> {
    try {
      const courses = await this.getAllCoursesForExport(filters);

      // build months list — parse as local dates to avoid UTC-offset month shift
      const [sy, sm, sd] = filters.startDate.split('-').map(Number);
      const [ey, em, ed] = filters.endDate.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      const months: { year: number; month: number }[] = [];
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        months.push({ year: cursor.getFullYear(), month: cursor.getMonth() });
        cursor.setMonth(cursor.getMonth() + 1);
      }

      // group by planID + courseID
      type Group = {
        planID: string;
        planName: string;
        courseID: string;
        courseName: string;
        courseCode: string;
        recs: YearlyCalendarCourseReport[];
      };

      const groupedMap = new Map<string, Group>();
      for (const c of courses) {
        const planID = c.planID || c.planName || '';
        const courseID = c.msLCMSCourseID || c.trLCMSPlanningCoursesID || '';
        const key = `${planID}|${courseID}`;
        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            planID,
            planName: c.planName || '-',
            courseID,
            courseName: c.courseName || '-',
            courseCode: c.courseCode || '',
            recs: []
          });
        }
        groupedMap.get(key)!.recs.push(c);
      }

      const groupsArray = Array.from(groupedMap.values()).sort(
        (a, b) =>
          (a.planName || '').localeCompare(b.planName || '') ||
          (a.courseName || '').localeCompare(b.courseName || '')
      );

      const wb = XLSX.utils.book_new();

      // Raw Data (sorted by plan name asc implicitly by groupsArray ordering)
      const rawData: any[] = [];
      for (const g of groupsArray) {
        g.recs.sort((r1, r2) => {
          const t1 = r1.planningStart ? new Date(r1.planningStart).getTime() : 0;
          const t2 = r2.planningStart ? new Date(r2.planningStart).getTime() : 0;
          return (t1 || 0) - (t2 || 0);
        });
        for (let i = 0; i < g.recs.length; i++) {
          const course = g.recs[i];
          rawData.push({
            'No.': rawData.length + 1,
            'Plan Name': g.planName,
            'Plan ID': g.planID,
            Batch: i + 1,
            'Course Name': g.courseName,
            'Course Code': g.courseCode,
            School: course.schoolName || '-',
            'Delivery Type': course.deliveryType || '-',
            'Mode of Delivery': course.modeOfDelivery || '-',
            'Course Status': course.statusCourse || '-',
            'Learning Hours': course.learningHours || 0,
            'Planning Start': course.planningStart || '',
            'Planning End': course.planningEnd || '',
            'Assessment Start': course.assessmentStartDate || '',
            'Assessment End': course.assessmentEndDate || '',
            'Learning Objectives': this.stripHtml(course.learningObjectives || ''),
            Notes: course.notes || ''
          });
        }
      }

      const wsRaw = XLSX.utils.json_to_sheet(rawData);
      wsRaw['!cols'] = [
        { wch: 5 },
        { wch: 30 },
        { wch: 12 },
        { wch: 8 },
        { wch: 30 },
        { wch: 15 },
        { wch: 25 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 40 },
        { wch: 30 }
      ];
      XLSX.utils.book_append_sheet(wb, wsRaw, 'Raw Data');

      // General View
      const monthHeaders = months.map((m) =>
        new Date(m.year, m.month, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' })
      );
      const generalHeader = ['Plan Name', 'Course Name', ...monthHeaders];
      const generalAoa: any[][] = [generalHeader];

      for (const g of groupsArray) {
        const row: any[] = [g.planName, g.courseName + (g.courseCode ? ` (${g.courseCode})` : '')];
        const monthBuckets: Set<number>[] = Array.from({ length: months.length }, () => new Set());

        for (const rec of g.recs) {
          const pushDate = (dStr?: string | null) => {
            if (!dStr) return;
            const d = new Date(dStr);
            if (isNaN(d.getTime())) return;
            const mi = months.findIndex(
              (mm) => mm.year === d.getFullYear() && mm.month === d.getMonth()
            );
            if (mi >= 0) monthBuckets[mi].add(d.getDate());
          };

          if (rec.planningStart && rec.planningEnd) {
            const s = new Date(rec.planningStart);
            const e = new Date(rec.planningEnd);
            if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
              const iter = new Date(s);
              while (iter <= e) {
                pushDate(iter.toISOString());
                iter.setDate(iter.getDate() + 1);
              }
            } else {
              pushDate(rec.planningStart);
            }
          } else {
            pushDate(rec.planningStart);
          }
        }

        for (let mi = 0; mi < months.length; mi++) {
          const days = Array.from(monthBuckets[mi]).sort((a, b) => a - b);
          row.push(days.length ? days.join(', ') : '');
        }

        generalAoa.push(row);
      }

      const wsGeneral = XLSX.utils.aoa_to_sheet(generalAoa);
      wsGeneral['!cols'] = [{ wch: 30 }, { wch: 40 }, ...monthHeaders.map(() => ({ wch: 18 }))];
      XLSX.utils.book_append_sheet(wb, wsGeneral, 'General View');

      // Per-month sheets
      for (const m of months) {
        const year = m.year;
        const monthIndex = m.month;
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0);
        const lastDay = monthEnd.getDate();
        const numberOfWeeks = Math.ceil(lastDay / 7);

        const header = ['Plan Name', 'Course Name'];
        for (let w = 1; w <= numberOfWeeks; w++) header.push(`Week ${w}`);
        const aoa: any[][] = [header];

        for (const g of groupsArray) {
          const row: any[] = [];
          row.push(g.planName);
          row.push(g.courseName + (g.courseCode ? ` (${g.courseCode})` : ''));

          const weekBuckets: Set<number>[] = Array.from({ length: numberOfWeeks }, () => new Set());

          for (const rec of g.recs) {
            const addDate = (dStr?: string | null) => {
              if (!dStr) return;
              const d = new Date(dStr);
              if (isNaN(d.getTime())) return;
              if (d.getFullYear() === year && d.getMonth() === monthIndex) {
                const day = d.getDate();
                const wk = Math.floor((day - 1) / 7);
                if (weekBuckets[wk]) weekBuckets[wk].add(day);
              }
            };

            if (rec.planningStart && rec.planningEnd) {
              const s = new Date(rec.planningStart);
              const e = new Date(rec.planningEnd);
              if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
                const iter = new Date(s);
                while (iter <= e) {
                  addDate(iter.toISOString());
                  iter.setDate(iter.getDate() + 1);
                }
              } else {
                addDate(rec.planningStart);
              }
            } else {
              addDate(rec.planningStart);
            }
          }

          for (let w = 0; w < numberOfWeeks; w++) {
            const days = Array.from(weekBuckets[w]).sort((a, b) => a - b);
            row.push(days.length ? days.join(', ') : '');
          }

          aoa.push(row);
        }

        const wsMonth = XLSX.utils.aoa_to_sheet(aoa);
        wsMonth['!cols'] = [
          { wch: 30 },
          { wch: 40 },
          ...Array.from({ length: numberOfWeeks }, () => ({ wch: 12 }))
        ];
        const sheetName = `${monthStart.toLocaleString('en-US', { month: 'short' })}_${year}`;
        XLSX.utils.book_append_sheet(wb, wsMonth, sheetName);
      }

      const dateRange = `${filters.startDate}_to_${filters.endDate}`;
      const defaultFilename = `yearly_calendar_course_report_calendarview_${dateRange}.xlsx`;
      XLSX.writeFile(wb, filename || defaultFilename);
    } catch {
      throw new Error('Failed to export calendar view to Excel');
    }
  }

  calculateStatistics(courses: YearlyCalendarCourseReport[]): YearlyCalendarReportStatistics {
    const stats: YearlyCalendarReportStatistics = {
      totalCourses: courses.length,
      plannedCourses: 0,
      canceledCourses: 0,
      droppedCourses: 0,
      totalLearningHours: 0,
      coursesByDeliveryType: {},
      coursesBySchool: {},
      coursesByStatus: {},
      monthlyDistribution: {}
    };

    courses.forEach((course) => {
      const courseStatus = course.statusCourse?.toLowerCase() || 'unknown';
      switch (courseStatus) {
        case 'planned':
          stats.plannedCourses++;
          break;
        case 'canceled':
          stats.canceledCourses++;
          break;
        case 'dropped':
          stats.droppedCourses++;
          break;
      }

      stats.totalLearningHours += course.learningHours || 0;

      const deliveryType = course.deliveryType || 'Unknown';
      stats.coursesByDeliveryType[deliveryType] =
        (stats.coursesByDeliveryType[deliveryType] || 0) + 1;

      const schoolName = course.schoolName || 'Unknown School';
      stats.coursesBySchool[schoolName] = (stats.coursesBySchool[schoolName] || 0) + 1;

      const statusForGrouping = course.statusCourse || 'Unknown';
      stats.coursesByStatus[statusForGrouping] =
        (stats.coursesByStatus[statusForGrouping] || 0) + 1;

      if (course.planningStart) {
        const d = new Date(course.planningStart);
        if (!isNaN(d.getTime())) {
          const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
          stats.monthlyDistribution[key] = (stats.monthlyDistribution[key] || 0) + 1;
        }
      }
    });

    return stats;
  }

  private stripHtml(html: string): string {
    if (!html) return '';

    // Create a temporary DOM element to safely decode HTML entities
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    // Get text content (this automatically decodes entities and strips tags)
    return (tempElement.textContent || tempElement.innerText || '').replace(/\s+/g, ' ').trim();
  }
}

const yearlyCalendarReportService = new YearlyCalendarReportService();

export { yearlyCalendarReportService };
export default yearlyCalendarReportService;
